import { isEmpty } from 'lodash';

import LEGACY_ORG_FEATURES from '~/server_shared/generated-types/LEGACY_ORG_FEATURES';

import {
  CONFIGURATION_FEATURES_NAMES,
  PAYMENT_METHOD_CONFIGURATION_DEFAULT_V3,
  PAYMENT_METHODS_DETAILS_DICT,
} from '../../Types';
import {
  isFeatureEnabled,
  isLegacyOrgFeatureEnabled,
  isMgmOrganization,
  isRwOrganization,
  stringCmp,
} from '../../Utils';
import { PERMISSION_ACTIONS, PERMISSION_VERBS } from '../core/Permissions/PermissionUtils';
import { getSuggestedReferenceSanitizer } from '../exposures/PaymentRequestContainer/paymentContainerUtils';
import { getPaymentMethodDisplayName } from '../exposures/PaymentRequestContainer/PaymentMethodUtils';
import useOrganization from '../OrganizationContext';

import { useCms } from './useCms';
import { useHasPermission } from './useHasPermission';
import { useOrganizationDetails } from './useOrganizationDetails';
import { useSubOrganization } from './useSubOrganization';

// Vitesse's published cap on Recipient Reference for both of its cheque routes (`Cheque` for our
// eCheck, `Postal Cheque` for our postal cheque). Kept in step with the server-side rules table in
// `server/modules/vitesse/internal/managers/vitesse_field_rules.py`.
const VITESSE_CHECK_DESCRIPTION_MAX_LENGTH = 50;

// Whether the SWIFT/BIC field is on for one payment method: false for a method that does not declare
// the field at all, otherwise the organization's saved toggle, falling back to the enum default.
// Shared by the current-method flag and the organization-wide one so both read the field from the
// same place and no method needs its own branch here.
const isSwiftBicEnabledForMethod = (paymentMethodDetails, methodConfiguration) =>
  paymentMethodDetails?.swift_bic
    ? (methodConfiguration?.swift_bic?.should_include ?? paymentMethodDetails.swift_bic.should_include ?? true)
    : false;

// Whether the field is on for at least one enabled method in a set of configurations. The contact
// Finance tab is not scoped to a payment method, so one enabled method offering it is enough.
const isSwiftBicEnabledForAnyOfMethods = (paymentMethodConfigurations) =>
  Boolean(
    paymentMethodConfigurations?.some(
      (paymentMethodConfig) =>
        !paymentMethodConfig.is_disabled &&
        isSwiftBicEnabledForMethod(
          PAYMENT_METHODS_DETAILS_DICT[paymentMethodConfig.payment_method],
          paymentMethodConfig.method_configuration
        )
    )
  );

export const usePaymentsConfiguration = (selectedPaymentMethod = undefined) => {
  const { organization } = useOrganizationDetails({ kind: 'sysconfig_with_client_fallback' });
  const { legacyExpensePaymentTypes, legacyIndemnityPaymentTypes, paymentsConfiguration = {} } = useOrganization();
  const { subOrganization } = useSubOrganization();
  const { user } = useCms();

  let paymentsMethodConfigurations;
  let enabledPaymentMethodConfigurations;
  let paymentMethods;
  let defaultPaymentMethod;
  let isUkBankTransferEnabledForAny;
  let isAuBankTransferEnabledForAny;
  let isIbanBankTransferEnabledForAny;
  let isNzBankTransferEnabledForAny;
  let isSwiftBicEnabledForAnyMethod;
  let isCommerceBankEnabledForAny;
  let isPaymentLabelsEnabledForAny;
  let uniquePaymentMethodDisplayNamesMap = new Map();
  const isSubReservesEnabled = paymentsConfiguration?.is_sub_reserves_enabled;
  const isSplitRoutingEnabled = Boolean(paymentsConfiguration?.is_split_routing_enabled);

  const isMultipleSubReservePaymentsEnabled = paymentsConfiguration?.is_multiple_sub_reserve_payments_enabled;
  const isPrefillReserveAmountsCapabilityEnabled = paymentsConfiguration?.is_prefill_reserve_amounts_capability_enabled;
  const isAutoFillMailToFromPayee = paymentsConfiguration?.is_payment_method_auto_fill_mail_to_from_payee ?? false;
  const isRequireVerificationForContactFinanceEdit = Boolean(
    paymentsConfiguration?.require_verification_for_contact_finance_edit
  );
  const isRequireVerificationForContactAuTfnEdit = Boolean(
    paymentsConfiguration?.require_verification_for_contact_au_tfn_edit
  );
  const hasContactFinanceApproverPermission = useHasPermission({
    action: PERMISSION_ACTIONS.CONTACT_FINANCE_APPROVER,
    verb: PERMISSION_VERBS.WRITE,
  });
  const hasContactTfnApproverPermission = useHasPermission({
    action: PERMISSION_ACTIONS.CONTACT_TFN_APPROVER,
    verb: PERMISSION_VERBS.WRITE,
  });
  const isContactFinanceApprovalTabEnabled =
    isRequireVerificationForContactFinanceEdit && hasContactFinanceApproverPermission;
  const isContactTfnApprovalTabEnabled = isRequireVerificationForContactAuTfnEdit && hasContactTfnApproverPermission;
  const contactAuTfnMandatoryCoverages = paymentsConfiguration?.contact_au_tfn_mandatory_coverages || [];
  const isTrustedPayeeEnabled =
    Boolean(paymentsConfiguration?.enable_trusted_payee_payments) &&
    Boolean(paymentsConfiguration?.require_approval_for_every_payment);

  // Multi-currency configuration
  const isMultiCurrencyEnabled = Boolean(paymentsConfiguration?.allow_claim_multi_currency);
  const multiCurrencyConfiguration = paymentsConfiguration?.multi_currency_configuration || {};

  // General recoveries configuration
  const isGeneralRecoveriesEnabled = Boolean(paymentsConfiguration?.is_general_recoveries_enabled);
  const generalRecoveriesConfiguration = paymentsConfiguration?.general_recoveries_configuration || {};
  const isSplitGeneralRecoveryEnabled = Boolean(paymentsConfiguration?.enable_split_general_recovery);

  // Extract currencies for current organizational context
  const getPaymentConfigurationCurrencies = () => {
    if (!isMultiCurrencyEnabled || isEmpty(multiCurrencyConfiguration)) {
      return [];
    }

    // If sub-organizations are enabled and we have a specific sub-organization
    if (
      organization?.sub_organizations_enabled &&
      subOrganization?.id &&
      multiCurrencyConfiguration.sub_organizations
    ) {
      // Look for the sub-organization in the new structured format
      const subOrgConfig = multiCurrencyConfiguration.sub_organizations.find(
        (config) => config?.sub_organization_id === subOrganization.id
      );

      // Validate the sub-org config before using it
      if (subOrgConfig) {
        return subOrgConfig.enabled ? subOrgConfig.currencies || [] : [];
      }

      // Fallback if sub-org config is malformed
      return [];
    }

    // Otherwise use organization-level configuration
    return multiCurrencyConfiguration.organization_currencies || [];
  };

  const paymentConfigurationCurrencies = getPaymentConfigurationCurrencies();

  const checkPaymentMethodEnabled = (paymentMethodConfigurations, paymentMethod) => {
    return isEmpty(paymentMethodConfigurations)
      ? false
      : paymentMethodConfigurations.some(
          (paymentMethodConfig) =>
            paymentMethodConfig.payment_method === paymentMethod && !paymentMethodConfig.is_disabled
        );
  };

  const setPaymentMethodsEnabledForAny = (paymentMethodConfigurations) => {
    isUkBankTransferEnabledForAny = checkPaymentMethodEnabled(paymentMethodConfigurations, 'uk_bank_transfer');
    isAuBankTransferEnabledForAny = checkPaymentMethodEnabled(paymentMethodConfigurations, 'au_bank_transfer');
    isIbanBankTransferEnabledForAny = checkPaymentMethodEnabled(paymentMethodConfigurations, 'iban_bank_transfer');
    isNzBankTransferEnabledForAny = checkPaymentMethodEnabled(paymentMethodConfigurations, 'nz_bank_transfer');
    isCommerceBankEnabledForAny = checkPaymentMethodEnabled(paymentMethodConfigurations, 'commerce_bank');
  };

  const setIsPaymentLabelsEnabledForAny = (paymentMethodConfigurations) => {
    isPaymentLabelsEnabledForAny = isEmpty(paymentMethodConfigurations)
      ? true
      : paymentMethodConfigurations.some(
          (paymentMethodConfig) =>
            paymentMethodConfig.method_configuration.use_expenses_labels ||
            paymentMethodConfig.method_configuration.use_indemnity_labels
        );
  };

  function filterAndSortPaymentMethodConfigs(paymentMethodConfigurations) {
    return paymentMethodConfigurations
      ?.filter((methodConfig) => !methodConfig.is_disabled)
      ?.sort((a, b) => {
        const displayNameA = a.is_custom_method
          ? a.payment_method_display_name
          : PAYMENT_METHODS_DETAILS_DICT[a.payment_method].display_name;

        const displayNameB = b.is_custom_method
          ? b.payment_method_display_name
          : PAYMENT_METHODS_DETAILS_DICT[b.payment_method].display_name;
        return stringCmp(displayNameA, displayNameB);
      });
  }

  function getDecoratedPaymentMethodConfig(methodConfig) {
    const decoratedMethodConfig = { ...methodConfig };

    decoratedMethodConfig.is_custom_method =
      PAYMENT_METHODS_DETAILS_DICT[methodConfig.payment_method]?.is_custom_method || false;
    decoratedMethodConfig.original_payment_method = decoratedMethodConfig.payment_method;

    if (decoratedMethodConfig.is_custom_method) {
      decoratedMethodConfig.payment_method = methodConfig.payment_method_external_id;
    }

    return decoratedMethodConfig;
  }

  let allUniqueEnabledPaymentMethodConfigs;

  if (!isEmpty(paymentsConfiguration?.payment_method_configurations)) {
    const decoratedConfigs = paymentsConfiguration?.payment_method_configurations?.map(getDecoratedPaymentMethodConfig);
    const filteredConfigs = filterAndSortPaymentMethodConfigs(decoratedConfigs);
    // There could be multiple configs of the same method but only one will be used.
    allUniqueEnabledPaymentMethodConfigs = [
      ...new Map(filteredConfigs.map((config) => [config.payment_method, config])).values(),
    ];
  } else {
    allUniqueEnabledPaymentMethodConfigs = [getDecoratedPaymentMethodConfig(PAYMENT_METHOD_CONFIGURATION_DEFAULT_V3)];
  }

  allUniqueEnabledPaymentMethodConfigs.forEach((paymentConfig) => {
    const paymentMethodName = paymentConfig.payment_method;
    const displayName = getPaymentMethodDisplayName(paymentConfig);
    uniquePaymentMethodDisplayNamesMap.set(paymentMethodName, displayName);
  });

  if (paymentsConfiguration?.is_org_level_configuration) {
    paymentsMethodConfigurations = !isEmpty(paymentsConfiguration?.payment_method_configurations)
      ? paymentsConfiguration?.payment_method_configurations
      : [PAYMENT_METHOD_CONFIGURATION_DEFAULT_V3];

    setPaymentMethodsEnabledForAny(paymentsConfiguration?.payment_method_configurations);
    setIsPaymentLabelsEnabledForAny(paymentsConfiguration?.payment_method_configurations);
    isSwiftBicEnabledForAnyMethod = isSwiftBicEnabledForAnyOfMethods(paymentsMethodConfigurations);
  } else {
    paymentsMethodConfigurations = !isEmpty(subOrganization?.payment_method_configurations)
      ? subOrganization?.payment_method_configurations
      : [PAYMENT_METHOD_CONFIGURATION_DEFAULT_V3];

    // A sub-organization serializes `payment_method_configurations` as null when it has no method
    // rows, so flatten past those rather than into them.
    const subOrgsPaymentMethods =
      organization?.sub_organizations?.flatMap((subOrg) => subOrg?.payment_method_configurations || []) || [];

    setPaymentMethodsEnabledForAny(subOrgsPaymentMethods);
    setIsPaymentLabelsEnabledForAny(paymentsMethodConfigurations);
    // Scoped to the sub-organization the screen is in: a contact opened from a claim carries that
    // claim's sub-organization, so one with the field switched off must not show it just because a
    // sibling sub-organization has it on. That holds for a sub-organization with no methods
    // configured too -- the scoped list is then the `check` default, which declares no SWIFT/BIC, so
    // the field hides, exactly as `shouldUseSwiftBic` already hides it in the Make Payment dialog.
    // Only the org-level Contacts screen, with no sub-organization in context at all, answers from
    // every sub-organization's methods.
    isSwiftBicEnabledForAnyMethod = subOrganization
      ? isSwiftBicEnabledForAnyOfMethods(paymentsMethodConfigurations)
      : isSwiftBicEnabledForAnyOfMethods(subOrgsPaymentMethods);
  }
  paymentsMethodConfigurations = paymentsMethodConfigurations.map(getDecoratedPaymentMethodConfig);

  paymentMethods = paymentsMethodConfigurations
    .filter((paymentMethod) => !paymentMethod.is_disabled)
    ?.sort((a, b) => (b.payment_method > a.payment_method ? -1 : 1))
    ?.map((paymentMethod) => paymentMethod.payment_method);

  defaultPaymentMethod = paymentMethods[0];
  enabledPaymentMethodConfigurations = filterAndSortPaymentMethodConfigs(paymentsMethodConfigurations);

  const currentPaymentMethod = selectedPaymentMethod ?? enabledPaymentMethodConfigurations[0]?.payment_method;
  const currentPaymentMethodConfiguration = paymentsMethodConfigurations?.find(
    (paymentMethodConfiguration) => paymentMethodConfiguration.payment_method === currentPaymentMethod
  );

  const currentPaymentMethodDetails =
    PAYMENT_METHODS_DETAILS_DICT[currentPaymentMethodConfiguration?.original_payment_method || currentPaymentMethod];

  const getPaymentMethodProperty = (property) => {
    return currentPaymentMethodConfiguration?.method_configuration[property];
  };

  const isCustomMethod = currentPaymentMethodConfiguration?.is_custom_method || false;
  const paymentMethodDisplayName = currentPaymentMethodConfiguration?.payment_method_display_name || '';
  const isPaymentTypeRequireMailToContactId = currentPaymentMethodDetails?.mail_to_contact?.should_include || false;
  const isLobPayment = paymentsConfiguration?.method === 'lob';
  const isOneIncPayment = currentPaymentMethod === 'one_inc';
  // eCheck is a cheque delivered by email and shares the check number/date the search filters key
  // off, so it counts as a check payment for those cheque-specific fields.
  const isCheckPayment = ['check', 'echeck'].includes(currentPaymentMethod);
  const shouldUseUKBankTransfer = ['uk_bank_transfer'].includes(currentPaymentMethod);
  const shouldUseIbanBankTransfer = 'iban_bank_transfer' === currentPaymentMethod;
  const shouldUseAuBankTransfer = 'au_bank_transfer' === currentPaymentMethod;
  const shouldUseNzBankTransfer = 'nz_bank_transfer' === currentPaymentMethod;
  const shouldUseUsAch = 'us_ach' === currentPaymentMethod;
  const isNzBankAccountExact16DigitsRequired = Boolean(getPaymentMethodProperty('nz_bank_account_exact_16_digits'));
  const shouldUseMail = isPaymentTypeRequireMailToContactId;
  const shouldUsePayToLine = currentPaymentMethodDetails?.pay_to_line?.should_include || false;
  const shouldUseReference = currentPaymentMethodDetails?.reference?.should_include || false;
  const referenceMaxLength = currentPaymentMethodDetails?.reference?.max_length || null;
  const shouldUseSwiftBic = isSwiftBicEnabledForMethod(
    currentPaymentMethodDetails,
    currentPaymentMethodConfiguration?.method_configuration
  );
  const shouldUseCheckDescription =
    (currentPaymentMethodDetails?.check_description?.should_include || false) &&
    !isLegacyOrgFeatureEnabled(organization, LEGACY_ORG_FEATURES.MGM_HIDE_CHECK_DESCRIPTION);
  // The cheque memo is sent to Vitesse verbatim as RecipientReference, which Vitesse caps at 50 on
  // both of its cheque routes — below the provider-agnostic 60 in enums. The server rejects anything
  // over 50 for a Vitesse-backed method, so without this an adjuster composes 51–60 characters inline
  // and only learns of the real cap on submit. Clamped rather than lowered globally: the enum limit is
  // provider-agnostic and non-Vitesse cheque organizations keep their configured value.
  const configuredCheckDescriptionLimit = currentPaymentMethodDetails?.check_description?.max_length;
  const checkDescriptionLimit =
    shouldUseCheckDescription &&
    (getPaymentMethodProperty('payment_provider') === 'vitesse' && configuredCheckDescriptionLimit
      ? Math.min(configuredCheckDescriptionLimit, VITESSE_CHECK_DESCRIPTION_MAX_LENGTH)
      : configuredCheckDescriptionLimit);
  // How to clean up a reference the dialog *suggests* (from the claim number, or from an invoice
  // number on the expenses path) so the provider behind this method accepts it. Identity for every
  // provider that has no such restriction; a reference the adjuster typed herself never goes through
  // it.
  const sanitizeSuggestedReference = getSuggestedReferenceSanitizer(currentPaymentMethodConfiguration);
  const defaultPayToLineLimit =
    (shouldUsePayToLine && currentPaymentMethodDetails?.pay_to_line?.default_max_length) || 0;

  const isRequireApprovalForEveryPayment = Boolean(paymentsConfiguration?.require_approval_for_every_payment);
  // Only narrows the `isRequireApprovalForEveryPayment` routing, so it is not ANDed with it here --
  // its single consumer already does. Mirrors the backend's `record_only_bypass`.
  const isSkipApprovalForRecordOnlyPayments = Boolean(paymentsConfiguration?.skip_approval_for_record_only_payments);
  const additionalUsersToApprove = paymentsConfiguration?.additional_approving_user_ids || [];
  const allowMultiplePendingPaymentRequests = Boolean(paymentsConfiguration?.allow_multiple_pending_payment_requests);
  const enableApprovalScreen = paymentsConfiguration?.enable_approval_screen;
  const isAllowedToApprove =
    user.is_org_level_supervisor ||
    user.is_manager ||
    (enableApprovalScreen && additionalUsersToApprove.includes(user.id));
  const isApprovalPaymentsScreenEnabled = enableApprovalScreen && isAllowedToApprove;
  const methodAlwaysRequiredFields = currentPaymentMethodDetails?.always_required_fields || [];
  const isPayeeEmailRequired =
    methodAlwaysRequiredFields.includes('email') ||
    currentPaymentMethodConfiguration?.method_configuration?.required_fields?.email ||
    false;
  const isPayeePhoneNumberRequired =
    currentPaymentMethodConfiguration?.method_configuration?.required_fields?.phone_number || false;
  const isPermissionsToSendCommunicationsRequired =
    currentPaymentMethodConfiguration?.method_configuration?.permissions_to_send_communication_required || false;
  const isDateOfBirthRequired =
    currentPaymentMethodConfiguration?.method_configuration?.required_fields?.date_of_birth || false;
  const duplicatePaymentsLevelOfVerification = paymentsConfiguration?.duplicate_payments_level_of_verification;
  const duplicatePaymentsAction = paymentsConfiguration?.duplicate_payments_action;
  const adjustReserveIncludesDeductible = paymentsConfiguration?.adjust_reserve_includes_deductible;
  const adjustReserveAfterPaymentCancellation = paymentsConfiguration?.adjust_reserve_after_payment_cancellation;

  const isConfigurableFnolEnabled = isFeatureEnabled(organization, CONFIGURATION_FEATURES_NAMES.CONFIGURABLE_FNOL);
  const allowIndemnityPaymentsOnlyForExistingPropertyOwner =
    (paymentsConfiguration?.allow_indemnity_payments_only_for_existing_property_owner && !isConfigurableFnolEnabled) ||
    false;

  const includeVatInPaymentScreen = paymentsConfiguration?.include_vat_in_payment_screen;
  const includeVatInFinancesSummary = paymentsConfiguration?.include_vat_in_finances_summary;
  // Effective value of the "Display net amounts" child toggle. Without payment-screen VAT there is no
  // VAT data, so net would equal gross - gating here keeps every display surface consistent with the
  // parent switch. System Configuration reads the raw config key so the stored value still round-trips.
  const displayNetAmounts = !!(
    paymentsConfiguration?.include_vat_in_payment_screen && paymentsConfiguration?.display_net_amounts
  );
  // Effective value of the "Reduce reserves by Net paid" child toggle, gated on the same parent as
  // displayNetAmounts above and mirroring the backend's should_reduce_reserves_by_net_paid. Every
  // surface that previews what a payment will do to a reserve has to net by this, or it shows a figure
  // the approval will not produce.
  const reduceReservesByNetPaid = !!(
    paymentsConfiguration?.include_vat_in_payment_screen && paymentsConfiguration?.reduce_reserves_by_net_paid
  );
  const show1099Fields = paymentsConfiguration?.enable_1099_fields;
  const isRequireTinForIndemnityPayees = paymentsConfiguration?.require_tin_for_indemnity_payees || false;

  const isExpensesSubReservesConfigEnabled = paymentsConfiguration?.is_expenses_sub_reserves_config_enabled || false;
  const expensesSubReservesConfig = paymentsConfiguration?.expenses_sub_reserves_config || {};

  const additionalUserIdsToSubmit = paymentsConfiguration?.additional_submitting_user_ids || [];
  const enableSubmissionScreen = paymentsConfiguration?.enable_submission_screen || false;
  const isAllowedToSubmit = additionalUserIdsToSubmit.includes(user.id);
  const isPaymentsSubmissionScreenEnabled = isCommerceBankEnabledForAny && enableSubmissionScreen && isAllowedToSubmit;

  const shouldDisablePaymentExceedingReserve = paymentsConfiguration?.should_disable_payment_exceeding_reserve || false;
  const shouldDisableAdjusterModifyingSelfPaymentLimits =
    paymentsConfiguration?.disable_adjuster_modifying_self_payment_limits || false;

  const canCurrentUserApprovePayment = (
    issuedByUserId,
    wasOfacHitFound,
    wasOfacHitApproved,
    userHasPermissionsOfacOverride,
    hasCoveringDelegatedAuthority = false
  ) => {
    if (user.id === issuedByUserId || (wasOfacHitFound && !wasOfacHitApproved && !userHasPermissionsOfacOverride)) {
      return false;
    }

    // Delegated authority makes approving level-based: a user whose remaining authority
    // covers the request amount may decide even if they are not a manager. Round-robin
    // routes an over-authority payment to a covering peer regardless of the approval
    // screen, so a covering level entitles even when the screen is on — mirrors the
    // backend decide gate.
    if (hasCoveringDelegatedAuthority) {
      return true;
    }

    if (!isRequireApprovalForEveryPayment) {
      return user.is_org_level_supervisor || user.is_manager;
    }

    return isAllowedToApprove;
  };
  const shouldEnableDocuments =
    getPaymentMethodProperty('enable_documents') ||
    PAYMENT_METHODS_DETAILS_DICT[currentPaymentMethod]?.['always_enable_documents'];
  const shouldEnableReissued = getPaymentMethodProperty('enable_reissued', true);
  const shouldMovePendingCancelUponCancellation = Boolean(
    currentPaymentMethodDetails?.always_move_to_pending_cancel_upon_cancellation ||
      getPaymentMethodProperty('enable_cancel_pending')
  );
  const mailToLimits = getPaymentMethodProperty('mail_to_limits');
  const shouldEnableRecordOnly =
    getPaymentMethodProperty('enable_record_only') ||
    isLegacyOrgFeatureEnabled(organization, LEGACY_ORG_FEATURES.MGM_FORCE_RECORD_ONLY_PAYMENTS);
  const prefillIndemnityReferenceWithClaimNumber =
    shouldUseReference && getPaymentMethodProperty('prefill_indemnity_reference_with_claim_number');
  const prefillExpensesReferenceWithInvoiceNumber =
    shouldUseReference && getPaymentMethodProperty('prefill_expenses_reference_with_invoice_number');
  const useContactDetailsReadonly = getPaymentMethodProperty('use_contact_details_readonly') || false;

  const shouldUseLegacyLabel = isMgmOrganization(organization) || isRwOrganization(organization);

  const expensePaymentTypes = shouldUseLegacyLabel
    ? legacyExpensePaymentTypes
    : getPaymentMethodProperty('expenses_labels');

  const indemnityPaymentTypes = shouldUseLegacyLabel
    ? legacyIndemnityPaymentTypes
    : getPaymentMethodProperty('indemnity_labels');

  const shouldUseIndemnityLabels = isMgmOrganization(organization)
    ? true
    : getPaymentMethodProperty('use_indemnity_labels');

  const shouldUseExpensesLabels =
    isMgmOrganization(organization) || isRwOrganization(organization)
      ? true
      : getPaymentMethodProperty('use_expenses_labels');

  const isExpensePaymentAllowedWithCoverageIssue =
    paymentsConfiguration?.enable_paying_expenses_if_coverage_issue_exists ?? false;

  const isPaymentAllowedOnClosedExposureForPayableType = (payableType) => {
    return payableType === 'expenses'
      ? paymentsConfiguration?.enable_paying_expenses_if_exposure_is_closed
      : paymentsConfiguration?.enable_paying_indemnity_if_exposure_is_closed;
  };

  const isPaymentLimitExistsForPayableType = (payableType) => {
    return payableType === 'expenses'
      ? paymentsConfiguration?.is_expenses_limit_for_closed_exposure_enabled
      : paymentsConfiguration?.is_indemnity_limit_for_closed_exposure_enabled;
  };

  const getPaymentLimitForPayableType = (payableType) => {
    return payableType === 'expenses'
      ? paymentsConfiguration?.expenses_limit_for_closed_exposure
      : paymentsConfiguration?.indemnity_limit_for_closed_exposure;
  };

  return {
    isLobPayment,
    isOneIncPayment,
    isCheckPayment,
    isRequireApprovalForEveryPayment,
    isSkipApprovalForRecordOnlyPayments,
    isApprovalPaymentsScreenEnabled,
    enableApprovalScreen,
    canCurrentUserApprovePayment,
    additionalUsersToApprove,
    shouldUseUKBankTransfer,
    shouldUseIbanBankTransfer,
    shouldUseAuBankTransfer,
    shouldUseNzBankTransfer,
    shouldUseUsAch,
    shouldUseMail,
    shouldUseReference,
    shouldUsePayToLine,
    shouldUseCheckDescription,
    checkDescriptionLimit,
    defaultPayToLineLimit,
    defaultPaymentMethod,
    isExpensePaymentAllowedWithCoverageIssue,
    isPaymentAllowedOnClosedExposureForPayableType,
    isPaymentLimitExistsForPayableType,
    getPaymentLimitForPayableType,
    shouldEnableReissued,
    mailToLimits,
    shouldEnableRecordOnly,
    prefillIndemnityReferenceWithClaimNumber,
    prefillExpensesReferenceWithInvoiceNumber,
    enabledPaymentMethodConfigurations,
    uniquePaymentMethodDisplayNamesMap,
    expensePaymentTypes,
    indemnityPaymentTypes,
    shouldUseIndemnityLabels,
    shouldUseExpensesLabels,
    allowMultiplePendingPaymentRequests,
    isPayeeEmailRequired,
    isPayeePhoneNumberRequired,
    isDateOfBirthRequired,
    isCustomMethod,
    paymentMethodDisplayName,
    referenceMaxLength,
    sanitizeSuggestedReference,
    duplicatePaymentsLevelOfVerification,
    duplicatePaymentsAction,
    allowIndemnityPaymentsOnlyForExistingPropertyOwner,
    isUkBankTransferEnabledForAny,
    isAuBankTransferEnabledForAny,
    isIbanBankTransferEnabledForAny,
    isNzBankTransferEnabledForAny,
    shouldUseSwiftBic,
    isNzBankAccountExact16DigitsRequired,
    isSwiftBicEnabledForAnyMethod,
    // The Payments tab's Fund Account Number column gate: the flag the server marshals the value
    // behind, read here off the same organization the other feature gates in this hook read.
    isFundAccountEngineEnabled: isFeatureEnabled(organization, CONFIGURATION_FEATURES_NAMES.FUND_ACCOUNT_ENGINE),
    includeVatInPaymentScreen,
    includeVatInFinancesSummary,
    displayNetAmounts,
    reduceReservesByNetPaid,
    show1099Fields,
    isRequireTinForIndemnityPayees,
    shouldEnableDocuments,
    shouldMovePendingCancelUponCancellation,
    isGeneralRecoveriesEnabled,
    generalRecoveriesConfiguration,
    isSplitGeneralRecoveryEnabled,
    paymentMethodConfiguration: currentPaymentMethodConfiguration,
    isExpensesSubReservesConfigEnabled,
    expensesSubReservesConfig,
    shouldDisablePaymentExceedingReserve,
    isPaymentsSubmissionScreenEnabled,
    isPaymentLabelsEnabledForAny,
    isSubReservesEnabled,
    isSplitRoutingEnabled,
    isMultipleSubReservePaymentsEnabled,
    adjustReserveIncludesDeductible,
    adjustReserveAfterPaymentCancellation,
    shouldDisableAdjusterModifyingSelfPaymentLimits,
    isPrefillReserveAmountsCapabilityEnabled,
    isAutoFillMailToFromPayee,
    isPermissionsToSendCommunicationsRequired,
    isMultiCurrencyEnabled,
    paymentConfigurationCurrencies,
    isRequireVerificationForContactFinanceEdit,
    isContactFinanceApprovalTabEnabled,
    isRequireVerificationForContactAuTfnEdit,
    isContactTfnApprovalTabEnabled,
    contactAuTfnMandatoryCoverages,
    isTrustedPayeeEnabled,
    useContactDetailsReadonly,
  };
};
